import React, { createContext, useContext, useState, ReactNode } from "react";

type Language = "en" | "bn";

interface Translations {
  [key: string]: { en: string; bn: string };
}

const translations: Translations = {
  // Navigation
  "nav.home": { en: "Home", bn: "হোম" },
  "nav.find": { en: "Find", bn: "খুঁজুন" },
  "nav.tasks": { en: "Tasks", bn: "টাস্ক" },
  "nav.meals": { en: "Meals", bn: "খাবার" },
  "nav.profile": { en: "Profile", bn: "প্রোফাইল" },
  "nav.inbox": { en: "Inbox", bn: "ইনবক্স" },

  // Dashboard
  "dashboard.greeting": { en: "Good Morning", bn: "সুপ্রভাত" },
  "dashboard.compliance": { en: "Compliance Score", bn: "কমপ্লায়েন্স স্কোর" },
  "dashboard.upcoming": { en: "Upcoming Appointment", bn: "আসন্ন অ্যাপয়েন্টমেন্ট" },
  "dashboard.tasks_today": { en: "Today's Tasks", bn: "আজকের টাস্ক" },
  "dashboard.weight_trend": { en: "Weight Trend", bn: "ওজনের প্রবণতা" },
  "dashboard.water": { en: "Water Intake", bn: "পানি পান" },
  "dashboard.quick_log": { en: "Quick Log", bn: "দ্রুত লগ" },

  // Find Dietitian
  "find.title": { en: "Find a Dietitian", bn: "ডায়েটিশিয়ান খুঁজুন" },
  "find.search": { en: "Search by name or specialty...", bn: "নাম বা বিশেষত্ব দিয়ে খুঁজুন..." },
  "find.book": { en: "Book Now", bn: "বুক করুন" },
  "find.experience": { en: "years exp.", bn: "বছরের অভিজ্ঞতা" },
  "find.consultation": { en: "Consultation", bn: "পরামর্শ" },
  "find.per_session": { en: "per session", bn: "প্রতি সেশন" },

  // Tasks
  "tasks.title": { en: "Your Tasks", bn: "আপনার টাস্ক" },
  "tasks.completed": { en: "Completed", bn: "সম্পন্ন" },
  "tasks.pending": { en: "Pending", bn: "অমীমাংসিত" },

  // Meals
  "meals.title": { en: "Meal Log", bn: "খাবারের লগ" },
  "meals.add": { en: "Add Meal", bn: "খাবার যোগ করুন" },
  "meals.breakfast": { en: "Breakfast", bn: "সকালের নাস্তা" },
  "meals.lunch": { en: "Lunch", bn: "দুপুরের খাবার" },
  "meals.dinner": { en: "Dinner", bn: "রাতের খাবার" },
  "meals.snack": { en: "Snack", bn: "নাস্তা" },
  "meals.ramadan_mode": { en: "Ramadan Mode", bn: "রমজান মোড" },

  // Prescription
  "rx.title": { en: "Prescription", bn: "প্রেসক্রিপশন" },
  "rx.morning": { en: "Morning", bn: "সকাল" },
  "rx.afternoon": { en: "Afternoon", bn: "দুপুর" },
  "rx.evening": { en: "Evening", bn: "সন্ধ্যা" },
  "rx.supplements": { en: "Supplements", bn: "সাপ্লিমেন্ট" },
  "rx.exercise": { en: "Exercise", bn: "ব্যায়াম" },
  "rx.download": { en: "Download PDF", bn: "পিডিএফ ডাউনলোড" },

  // Common
  "common.see_all": { en: "See All", bn: "সব দেখুন" },
  "common.cancel": { en: "Cancel", bn: "বাতিল" },
  "common.save": { en: "Save", bn: "সংরক্ষণ" },
  "common.glasses": { en: "glasses", bn: "গ্লাস" },
};

interface LanguageContextType {
  language: Language;
  toggleLanguage: () => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguage] = useState<Language>("en");

  const toggleLanguage = () => setLanguage((prev) => (prev === "en" ? "bn" : "en"));

  const t = (key: string): string => {
    return translations[key]?.[language] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) throw new Error("useLanguage must be used within LanguageProvider");
  return context;
};
